import { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useIdleLogout } from "./useIdleLogout";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  onboardingCompleted: boolean | null;
  refreshOnboarding: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ session: null, user: null, loading: true, onboardingCompleted: null, refreshOnboarding: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);

  const checkOnboarding = useCallback(async (userId?: string) => {
    const uid = userId || session?.user?.id;
    if (!uid) return;
    const { data } = await supabase
      .from("profiles")
      .select("onboarding_completed")
      .eq("user_id", uid)
      .maybeSingle();
    setOnboardingCompleted(data?.onboarding_completed ?? false);
  }, [session]);

  const refreshOnboarding = useCallback(async () => {
    await checkOnboarding();
  }, [checkOnboarding]);

  useEffect(() => {
    let initialised = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
      if (initialised) {
        // Only run on subsequent auth events (sign-in, sign-out, token refresh)
        if (session?.user) {
          checkOnboarding(session.user.id);
        } else {
          setOnboardingCompleted(null);
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      initialised = true;
      if (session?.user) {
        checkOnboarding(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useIdleLogout(!!session);

  const value = useMemo(
    () => ({ session, user: session?.user ?? null, loading, onboardingCompleted, refreshOnboarding }),
    [session, loading, onboardingCompleted, refreshOnboarding],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
